import { html, LitElement, nothing } from "lit"
import { createRef, ref } from "lit/directives/ref.js"
import { classMap } from "lit/directives/class-map.js"
import { styleMap } from "lit/directives/style-map.js"

export class CustomDialog extends LitElement {
  static get properties() {
    return {
      open: { type: Boolean },
      position: { type: String },
      dialogId: { type: String }, // Used to open dialog from URL
      hasCloseButton: { type: String },
      canCancel: { type: String },
      dialogStyles: { type: Object },
    }
  }

  connectedCallback() {
    super.connectedCallback()
    this.canCancel = this.canCancel !== "false"
  }

  disconnectedCallback() {
    this.onCloseModal()
    super.disconnectedCallback()
  }

  /** @type {ref<HTMLDialogElement>} */
  dialogRef = createRef()
  slots = ["dialog-title", "dialog-content", "dialog-actions"]

  firstUpdated() {
    this.style.display = "block"
    this.setupDialogActions()
    this.hideSlotContents()

    if (this.canCancel) {
      this.dialogRef.value.addEventListener("click", (event) => {
        if (event.target.tagName === "DIALOG") {
          this.dialogRef.value.close()
        }
      })
    }

    this.dialogRef.value.addEventListener("close", () => {
      this.onCloseModal()
    })

    this.openDialogBasedOnUrlParams()
    this.openDialogBasedOnProperty()
  }

  setupDialogActions() {
    const dialogActions = this.shadowRoot.querySelector("slot[name=dialog-actions]")
    if (dialogActions && dialogActions.assignedNodes().length > 0) {
      dialogActions.assignedNodes()[0].classList.add("dialog__actions")
    } else {
      this.slots = this.slots.filter((ref) => ref !== "dialog-actions")
      dialogActions.parentElement.remove()
    }
  }

  openDialogBasedOnUrlParams() {
    if (
      this.dialogId &&
      new URLSearchParams(globalThis.location.search).get("open-dialog") ===
        this.dialogId
    ) {
      this.showModal()
    }
  }

  openDialogBasedOnProperty() {
    if (this.open) {
      this.showModal()
    }
  }

  showModal() {
    if (this.dialogRef.value && !this.dialogRef.value.open) {
      this.dialogRef.value.showModal()
      this.onModalOpened()
      this.showSlotContents()
    }
    document.body.style.overflow = "hidden"
    document.documentElement.style.scrollbarGutter = "stable"
  }

  getCloseButton() {
    if (this.hasCloseButton !== "false" && this.canCancel) {
      return html`<button
        class="cross"
        style="grid-area: close;"
        @click="${this.close}"
      ></button>`
    }
    return nothing
  }

  close() {
    if (this.dialogRef.value && this.dialogRef.value.open) {
      this.dialogRef.value.close()
      this.onCloseModal()
    }
  }

  onModalOpened() {
    this.dispatchEvent(
      new CustomEvent("modal-opened", {
        bubbles: true,
        composed: true,
      }),
    )
  }

  onCloseModal() {
    document.body.style.overflow = ""
    document.documentElement.style.scrollbarGutter = ""
    this.hideSlotContents()
  }

  hideSlotContents() {
    this.slots.forEach((ref) => {
      const slot = this.shadowRoot.querySelector(`slot[name=${ref}]`)
      slot.assignedElements().forEach((element) => (element.style.opacity = "0"))
    })
  }

  showSlotContents() {
    this.slots.forEach((ref) => {
      const slot = this.shadowRoot.querySelector(`slot[name=${ref}]`)
      slot.assignedElements().forEach((element) => (element.style.opacity = "1"))
    })
  }

  render() {
    return html`
      <link rel="stylesheet" href="${globalThis.styleFilePath}" />
      <slot name="trigger" @click="${this.showModal}"></slot>
      <dialog
        class="dialog
                     ${classMap({ "dialog--bottom": this.position === "bottom" })}
              "
        style="${styleMap(this.dialogStyles ? this.dialogStyles : {})}"
        ${ref(this.dialogRef)}
      >
        <div class="dialog__card">
          <div class="dialog__head">
            <slot name="dialog-title" style="grid-area: title;"></slot>
            ${this.getCloseButton()}
          </div>
          <div class="dialog__content">
            <slot name="dialog-content"></slot>
          </div>
          <div>
            <slot name="dialog-actions"></slot>
          </div>
        </div>
      </dialog>
    `
  }
}

globalThis.customElements.define("custom-dialog", CustomDialog)
